import Vue from 'vue';
import Vuex from 'vuex';

import AppConfig, { IConfig } from './Scripts/AppConfig';

import { Route } from 'vue-router';

import { ISearchResult, IFilter, SearchType, IProgram, IFacetValue, ICourse, isCourse } from '@/catalog-search/Scripts/ISearchService';
import { FacetMetadata } from '@/catalog-search/Scripts/FacetMetadata';

import { PrintContext } from '@/catalog-search/Scripts/printContext';
import { SessionStorage } from './Scripts/SessionStorage';
import { flatten } from './Scripts/QueryString';
import { PeriodFacet } from './Scripts/FacetDefinition';

import { ISearchState } from './Scripts/ISearchState';
import { SearchActions } from './Scripts/SearchActions';

Vue.use(Vuex);

export enum Getters {
	applicationName = 'applicationName',
	administrationOrganization = 'administrationOrganization',
}

export default new Vuex.Store<ISearchState>({
	state: {
		loading: false,

		printContext: null as PrintContext | null,

		searchType: SearchType.Program,

		result: null as ISearchResult | null,
		query: '',
		submittedQuery: '',
		queryNoMatch: false,
		filters: [] as IFilter[],
		pageSize: 10,
		page: 1,
		sort: 'relevance',

		favorites: [] as IProgram[],
		courseFavorites: [] as ICourse[],

		config: {} as IConfig,

		currentFacet: '',
		currentFacetList: [] as IFacetValue[],

		currentTermList: [] as IFacetValue[],

		dismissedFavWarning: false,
		dismissedDisclaimer: true,

		academicPeriodExpanded: false
	},
	mutations: {
		setPrintContext (state, printContext: PrintContext): void {
			state.printContext = printContext;
		},

		setLoading (state, loading: boolean): void {
			state.loading = loading;
		},

		setSearchType (state, searchType: SearchType): void {
			state.searchType = searchType;
		},

		setResult (state, result: ISearchResult): void {
			state.result = result;
		},

		setQueryNoMatch (state, value): void {
			state.queryNoMatch = value;
		},

		setSort (state, key): void {
			state.sort = key;
		},

		changeQuery (state, query): void {
			state.query = query;
		},

		addFilter (state, filter: IFilter): void {
			const index = state.filters.findIndex((x) => x.key === filter.key);
			if (index > -1) {
				state.filters.splice(index, 1);
			}

			state.filters.push(filter);
		},

		removeFilter (state, filter: IFilter): void {
			const index = state.filters.findIndex((x) => x.key === filter.key);
			if (index > -1) {
				state.filters.splice(index, 1);
			}
		},

		clearFilters (state): void {
			state.filters = [];
		},

		changePage (state, page: number): void {
			state.page = page;
		},

		setPageSize (state, size: number): void {
			state.pageSize = size;
		},

		removeFavorite (state, favorite: IProgram | ICourse): void {
			const collection = isCourse(favorite) ? state.courseFavorites : state.favorites;

			const index = collection.findIndex((p: IProgram | ICourse) => p.id === favorite.id);

			if (index > -1) {
				collection.splice(index, 1);
			}

			SessionStorage.removeFavorite(favorite);
		},

		addFavorite (state, favorite: IProgram | ICourse): void {
			if (isCourse(favorite)) {
				state.courseFavorites.push(favorite);
			} else {
				state.favorites.push(favorite);
			}

			SessionStorage.addFavorite(favorite);
		},

		loadFavorites (state): void {
			state.favorites = SessionStorage.getProgramFavorites();
			state.courseFavorites = SessionStorage.getCourseFavorites();
		},

		setConfig (state, config): void {
			state.config = config;
		},

		resetCurrentFacet (state): void {
			state.currentFacet = '';
			state.currentFacetList = [];
		},

		dismissFavWarning (state): void {
			state.dismissedFavWarning = true;
			SessionStorage.dismissedFavWarning = true;
		},

		loadDismissedFavWarning (state): void {
			state.dismissedFavWarning = SessionStorage.dismissedFavWarning;
		},

		dismissDisclaimer (state): void {
			state.dismissedDisclaimer = true;
			SessionStorage.dismissedDisclaimer = true;
		},

		loadDismissedDisclaimer (state): void {
			state.dismissedDisclaimer = SessionStorage.dismissedDisclaimer;
		},

		toggleAcademicPeriodExpanded (state): void {
			state.academicPeriodExpanded = !state.academicPeriodExpanded;
		}
	},
	actions: {
		initFromRoute (context, route: Route): void {
			const queryString = route.query;

			context.commit('clearFilters');

			const searchType = queryString.search === 'courses' ? SearchType.Course : SearchType.Program;
			context.commit('setSearchType', searchType);

			context.commit('changeQuery', flatten(queryString.keywords));

			FacetMetadata.list(searchType).forEach((f) => {
				const param = queryString[f.query];
				if (param) {
					const filter = f.createFilter(param);
					context.commit('addFilter', filter);
				}
			});

			if (searchType === SearchType.Course) {
				const period = queryString.term || AppConfig.defaultPeriod;

				if (period) {
					const facet = new PeriodFacet('yearTerm', 'term', 'Period', undefined);
					const filter = facet.createFilter(period);
					context.commit('addFilter', filter);
				}
			}

			context.commit('setSort', flatten(queryString.sort));

			context.commit('setPageSize', +queryString.pageSize || 10);
			context.commit('changePage', +queryString.page || 1);

			context.commit('loadFavorites');

			context.commit('loadDismissedFavWarning');
			context.commit('loadDismissedDisclaimer');

			context.commit('setConfig', AppConfig);
		},

		async performSearch (context): Promise<void> {
			const state = context.state;

			context.commit('setLoading', true);

			try {
				const actions = new SearchActions();

				if (state.searchType === SearchType.Course) {
					state.currentTermList = await actions.GetAvailablePeriods(state);

					const anyPeriodsAvailable = state.currentTermList && state.currentTermList.length;

					if (anyPeriodsAvailable) {
						// Handle what should be a super rare condition in which the current period has no
						// approved courses yet other periods do have courses
						const periodFilter = state.filters.find((f) => f.key === 'yearTerm');

						if (periodFilter && !state.currentTermList.some((p) => p.value === periodFilter.value)) {
							// the period is NOT on the list of periods with results so replace it with (a random) one that is
							const facet = new PeriodFacet('yearTerm', 'term', 'Period', undefined);

							const filter = facet.createFilter(state.currentTermList[0].value);
							context.commit('addFilter', filter);
						}
					}
				}

				const search = await actions.SearchIndex(state);

				context.commit('setQueryNoMatch', search.fuzzy);
				context.commit('setResult', search.result);

				state.submittedQuery = state.query;
			} finally {
				context.commit('setLoading', false);
			}
		},

		async performFacet (context, column: string): Promise<void> {
			const state = context.state;

			const actions = new SearchActions();

			state.currentFacetList = await actions.ExploreFacet(state, column);
			state.currentFacet = column;
		}
	},
	getters: {
		[Getters.administrationOrganization]: ({ config }: { config: IConfig }) => config.administrationOrganization,
		[Getters.applicationName]: ({ config }: { config: IConfig }) => config.applicationName
	}
});
